/* eslint-disable @typescript-eslint/no-redeclare */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import CustomLoader from "../ui/custom-loader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import dataProvider from "../../authentication/dataProvider";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";
import { services } from "../../services";
import { actions } from "../../actions";
import { web3 } from "../../web3";

const ASSET_STATUS_LIST = [
  "UPCOMING",
  "ACTIVE",
  "PAUSED",
  "SOLDOUT",
  "EXPIRED",
  "REFUND",
  "REJECTED",
];

const UpdateYields = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [rewardTransactionLoading, setRewardTransactionLoading] =
    useState(false);
  const [priceTransactionLoading, setPriceTransactionLoading] = useState(false);
  const [assets, setAssets] = useState<any[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);
  const [selectedAssetDetails, setSelectedAssetDetails] = useState<any | null>(
    null,
  );
  const [yieldValue, setYieldValue] = useState<string>("");
  const [rewardValue, setRewardValue] = useState<string>("");
  const [priceValue, setPriceValue] = useState<string>("");
  const [web3Data, setWeb3Data] = useState(null);
  const [tokenContract, setTokenContract] = useState(null);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [currentYield, setCurrentYield] = useState<string>("0");
  const [tokenBalance, setTokenBalance] = useState<string>("0");
  const [currentPrice, setCurrentPrice] = useState<string>("0");
  const [assetStatus, setAssetStatus] = useState<string>("");
  const [currentStatus, setCurrentStatus] = useState<number | null>(null);
  const [statusTransactionLoading, setStatusTransactionLoading] =
    useState(false);
  const [assetImplimentationContract, setAssetImplimentationContract] =
    useState(null);

  useEffect(() => {
    async function initializeData() {
      try {
        setLoading(true);

        // First initialize web3
        const web3Data = await services.getWeb3();
        if (!web3Data || !web3Data.accounts || !web3Data.accounts[0]) {
          throw new Error("Please connect your wallet first");
        }
        setWeb3Data(web3Data);

        const tokenInstance = await actions.getTokenContract();
        if (!tokenInstance) {
          throw new Error("Failed to initialize token contract");
        }

        setTokenContract(tokenInstance);

        // Then get contract instance
        if (selectedAssetDetails?.assetAddress) {
          const contractInstance = await actions.getPropertyAssetContract(
            selectedAssetDetails.assetAddress,
          );
          if (!contractInstance) {
            throw new Error("Failed to initialize contract");
          }
          setAssetImplimentationContract(contractInstance);

          // Fetch current yield from contract
          const yieldResult: string = await contractInstance.methods
            .assetYield()
            .call();
          const priceResult: string = await contractInstance.methods
            .assetPrice()
            .call();

          // Convert the yield value
          const yieldNumber = Number(yieldResult);
          const priceNumber = web3.utils.fromWei(priceResult, "ether");

          setCurrentPrice(priceNumber);
          setCurrentYield(yieldNumber.toString());

          // Fetch current status from contract
          const statusResult: string = await contractInstance.methods
            .getAssetStatus()
            .call();
          setCurrentStatus(Number(statusResult));

          // Fetch token balance
          await fetchTokenBalance();
        }

        setLoading(false);
      } catch (error) {
        toast({
          description: "Failed to initialize: " + error.message,
          variant: "destructive",
        });
        setLoading(false);
      }
    }

    initializeData();
  }, [selectedAssetDetails, toast]);

  useEffect(() => {
    const fetchAssets = async () => {
      setLoading(true);
      try {
        const response = await dataProvider.getList("assets", {
          pagination: { page: 1, perPage: 100 }, // Get all assets at once
          sort: { field: "id", order: "DESC" },
          filter: {},
        });

        if (Array.isArray(response.data)) {
          setAssets(response.data);
        } else {
          setAssets([]);
        }
      } catch (error) {
        setAssets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAssets();
  }, []);

  // Function to fetch token balance
  const fetchTokenBalance = async () => {
    try {
      if (!tokenContract || !selectedAssetDetails?.assetAddress) {
        return;
      }

      const assetAddress = selectedAssetDetails.assetAddress;
      const balance = await tokenContract.methods
        .balanceOf(assetAddress)
        .call();
      const balanceInEther = web3.utils.fromWei(balance, "ether");
      setTokenBalance(balanceInEther);
    } catch (error) {
      toast({
        description: "Failed to fetch token balance",
        variant: "destructive",
      });
    }
  };

  const addReward = async () => {
    if (!selectedAssetDetails) {
      toast({
        title: "Error",
        variant: "destructive",
        description: "Please select an asset first",
      });
      return;
    }

    const numericValue = parseFloat(rewardValue);
    if (isNaN(numericValue) || numericValue <= 0) {
      toast({
        title: "Error",
        variant: "destructive",
        description: "Please enter a valid positive number",
      });
      return;
    }

    try {
      if (!web3Data || !web3Data.accounts[0]) {
        toast({
          title: "Error",
          variant: "destructive",
          description: "Please connect your wallet first.",
        });
        return;
      }

      if (!tokenContract || !assetImplimentationContract) {
        toast({
          title: "Error",
          variant: "destructive",
          description:
            "Contracts not initialized. Please check your wallet connection.",
        });
        return;
      }

      setRewardTransactionLoading(true);
      const amountInWei = web3.utils.toWei(rewardValue.toString(), "ether");

      const getAllowance = await tokenContract.methods
        .allowance(web3Data.accounts[0], selectedAssetDetails.assetAddress)
        .call();

      if (
        Number(getAllowance) === 0 ||
        Number(getAllowance) < Number(amountInWei)
      ) {
        toast({
          title: "Approval Required",
          description: "Please approve token spending...",
          variant: "default",
          duration: 5000,
        });

        const maxUint256 =
          "115792089237316195423570985008687907853269984665640564039457584007913129639935";

        await tokenContract.methods
          .approve(selectedAssetDetails.assetAddress, maxUint256)
          .send({ from: web3Data.accounts[0] })
          .on("transactionHash", (hash) => {
            toast({
              title: "Approval Pending",
              description: `Approval transaction submitted. Hash: ${hash.slice(0, 10)}...`,
              variant: "default",
              duration: 5000,
            });
          })
          .on("receipt", () => {
            toast({
              title: "Success",
              variant: "default",
              description: "Token approval successful. Processing reward...",
              duration: 5000,
            });
          })
          .on("error", (error) => {
            setRewardTransactionLoading(false);
            throw new Error(error.message || "Failed to approve tokens");
          });
      }

      await assetImplimentationContract.methods
        .addReward(amountInWei)
        .send({ from: web3Data.accounts[0] })
        .on("transactionHash", (hash) => {
          toast({
            title: "Transaction Initiated",
            description: `Reward transaction submitted. Hash: ${hash.slice(0, 10)}...`,
            variant: "default",
            duration: 5000,
          });
        })
        .on("receipt", async () => {
          setRewardValue("");
          // Refresh asset details after successful update
          const response = await dataProvider.getOne("assets", {
            id: selectedAsset,
          });
          setSelectedAssetDetails(response.data);
          await fetchTokenBalance();
          setRewardTransactionLoading(false);
          toast({
            title: "Success",
            variant: "default",
            description: `Successfully added ${rewardValue} USDT as reward`,
            duration: 5000,
          });
        })
        .on("error", (error) => {
          setRewardTransactionLoading(false);
          toast({
            title: "Transaction Failed",
            variant: "destructive",
            description: error.message || "Failed to add reward",
            duration: 5000,
          });
        });
    } catch (error) {
      setRewardTransactionLoading(false);
      toast({
        title: "Error",
        variant: "destructive",
        description:
          "Failed to add reward: " + (error.message || "Unknown error"),
        duration: 5000,
      });
    }
  };

  const handleYieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setYieldValue(value);
  };

  const handleRewardChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRewardValue(value);
  };

  const updateYield = async () => {
    const numericValue = parseFloat(yieldValue);

    if (!selectedAssetDetails) {
      toast({
        title: "Error",
        description: "Please select an asset first",
        variant: "destructive",
      });
      return;
    }

    if (isNaN(numericValue)) {
      toast({
        title: "Error",
        description: "Please enter a valid number",
        variant: "destructive",
      });
      return;
    }

    if (numericValue < 0 || numericValue > 100) {
      toast({
        title: "Error",
        description: "Yield must be between 0 and 100",
        variant: "destructive",
      });
      return;
    }

    try {
      if (!web3Data || !web3Data.accounts[0]) {
        toast({
          title: "Error",
          description: "Please connect your wallet first.",
          variant: "destructive",
        });
        return;
      }

      if (!assetImplimentationContract) {
        toast({
          title: "Error",
          description:
            "Contract not initialized. Please check your wallet connection.",
          variant: "destructive",
        });
        return;
      }

      setTransactionLoading(true);

      await assetImplimentationContract.methods
        .updateAssetYield(yieldValue)
        .send({ from: web3Data.accounts[0] })
        .on("transactionHash", (hash) => {
          toast({
            title: "Transaction Initiated",
            description: `Your yield update transaction has been submitted. Transaction hash: ${hash.slice(0, 10)}...`,
            variant: "default",
            duration: 5000,
          });
        })
        .on("receipt", async () => {
          setYieldValue("");
          // Refresh asset details after successful update
          const response = await dataProvider.getOne("assets", {
            id: selectedAsset,
          });
          setSelectedAssetDetails(response.data);
          setTransactionLoading(false);
          toast({
            title: "Success",
            description: `Yield has been successfully updated to ${yieldValue}%`,
            variant: "default",
            duration: 5000,
          });
        })
        .on("error", (error) => {
          setTransactionLoading(false);
          toast({
            title: "Transaction Failed",
            description:
              error.message || "Failed to update yield. Please try again.",
            variant: "destructive",
            duration: 5000,
          });
        });
    } catch (error) {
      setTransactionLoading(false);
      toast({
        title: "Error",
        description:
          "Failed to update yield: " +
          (error.message || "Unknown error. Please try again."),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPriceValue(value);
  };

  const updatePrice = async () => {
    const numericValue = parseFloat(priceValue);

    if (!selectedAssetDetails) {
      toast({
        title: "Error",
        description: "Please select an asset first",
        variant: "destructive",
      });
      return;
    }

    if (isNaN(numericValue) || numericValue <= 0) {
      toast({
        title: "Error",
        description: "Please enter a valid positive number",
        variant: "destructive",
      });
      return;
    }

    try {
      if (!web3Data || !web3Data.accounts[0]) {
        toast({
          title: "Error",
          description: "Please connect your wallet first.",
          variant: "destructive",
        });
        return;
      }

      if (!assetImplimentationContract) {
        toast({
          title: "Error",
          description:
            "Contract not initialized. Please check your wallet connection.",
          variant: "destructive",
        });
        return;
      }

      setPriceTransactionLoading(true);
      const priceInWei = web3.utils.toWei(priceValue.toString(), "ether");

      await assetImplimentationContract.methods
        .updateAssetPrice(priceInWei)
        .send({ from: web3Data.accounts[0] })
        .on("transactionHash", (hash) => {
          toast({
            title: "Transaction Initiated",
            description: `Your price update transaction has been submitted. Transaction hash: ${hash.slice(0, 10)}...`,
            variant: "default",
            duration: 5000,
          });
        })
        .on("receipt", async () => {
          setPriceValue("");
          // Refresh asset details after successful update
          const response = await dataProvider.getOne("assets", {
            id: selectedAsset,
          });
          setSelectedAssetDetails(response.data);
          const newPrice = await assetImplimentationContract.methods
            .assetPrice()
            .call();
          setCurrentPrice(web3.utils.fromWei(newPrice, "ether"));
          setPriceTransactionLoading(false);
          toast({
            title: "Success",
            description: `Price has been successfully updated to ${priceValue} USDT`,
            variant: "default",
            duration: 5000,
          });
        })
        .on("error", (error) => {
          setPriceTransactionLoading(false);
          toast({
            title: "Transaction Failed",
            description:
              error.message || "Failed to update price. Please try again.",
            variant: "destructive",
            duration: 5000,
          });
        });
    } catch (error) {
      setPriceTransactionLoading(false);
      toast({
        title: "Error",
        description:
          "Failed to update price: " +
          (error.message || "Unknown error. Please try again."),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleAssetSelect = async (value: string) => {
    if (!value) return;
    setSelectedAsset(value);
    try {
      const response = await dataProvider.getOne("assets", { id: value }); // Debug log
      setSelectedAssetDetails(response.data);
    } catch (error) {}
  };

  const updateAssetStatus = async () => {
    if (!selectedAssetDetails) {
      toast({
        title: "Error",
        description: "Please select an asset first",
        variant: "destructive",
      });
      return;
    }

    const numericStatus = parseInt(assetStatus, 10);
    if (isNaN(numericStatus)) {
      toast({
        title: "Error",
        description: "Please enter a valid status (number)",
        variant: "destructive",
      });
      return;
    }

    try {
      if (!web3Data || !web3Data.accounts[0]) {
        toast({
          title: "Error",
          description: "Please connect your wallet first.",
          variant: "destructive",
        });
        return;
      }

      if (!assetImplimentationContract) {
        toast({
          title: "Error",
          description:
            "Contract not initialized. Please check your wallet connection.",
          variant: "destructive",
        });
        return;
      }

      setStatusTransactionLoading(true);

      await assetImplimentationContract.methods
        .updateAssetStatus(numericStatus)
        .send({ from: web3Data.accounts[0] })
        .on("transactionHash", (hash) => {
          toast({
            title: "Transaction Initiated",
            description: `Asset status update transaction submitted. Hash: ${hash.slice(0, 10)}...`,
            variant: "default",
            duration: 5000,
          });
        })
        .on("receipt", async () => {
          setAssetStatus("");
          // Optionally refresh asset details
          const response = await dataProvider.getOne("assets", {
            id: selectedAsset,
          });
          setSelectedAssetDetails(response.data);

          // Refresh current status
          if (assetImplimentationContract) {
            const statusResult = await assetImplimentationContract.methods
              .getAssetStatus()
              .call();
            setCurrentStatus(Number(statusResult));
          }

          setStatusTransactionLoading(false);
          toast({
            title: "Success",
            description: `Asset status has been successfully updated.`,
            variant: "default",
            duration: 5000,
          });
        })
        .on("error", (error) => {
          setStatusTransactionLoading(false);
          toast({
            title: "Transaction Failed",
            description:
              error.message ||
              "Failed to update asset status. Please try again.",
            variant: "destructive",
            duration: 5000,
          });
        });
    } catch (error) {
      setStatusTransactionLoading(false);
      toast({
        title: "Error",
        description:
          "Failed to update asset status: " +
          (error.message || "Unknown error. Please try again."),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <CustomLoader size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4  p-6 rounded-lg">
      <h2 className="text-2xl text-black font-bold">Update Yields Details</h2>

      <div className="rounded-md border p-4">
        <div className="space-y-4">
          <div className="flex flex-col space-y-2">
            <label htmlFor="asset-select" className="text-sm font-medium">
              Select Asset
            </label>
            <Select
              onValueChange={handleAssetSelect}
              value={selectedAsset || undefined}
              onOpenChange={(open) => setIsSelectOpen(open)}
            >
              <SelectTrigger className="w-full relative">
                <SelectValue placeholder="Select an asset" />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${isSelectOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </SelectTrigger>
              <SelectContent>
                {assets && assets.length > 0 ? (
                  assets.map((asset) =>
                    asset.id ? (
                      <SelectItem key={asset.id} value={asset.id.toString()}>
                        {asset.assetName}
                      </SelectItem>
                    ) : null,
                  )
                ) : (
                  <SelectItem value="no-assets" disabled>
                    No assets available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-6">
            <div className="bg-background  rounded-lg p-6 transition-colors">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-normal text-surface-2">
                  Update Yield
                </h4>
                {selectedAssetDetails && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-surface-2">
                      Current Annual Yield:
                    </span>
                    <span className="text-sm font-semibold text-primary bg-secondary px-2 py-1 rounded-md">
                      {parseFloat(currentYield)}%
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-6 mt-2">
                <Input
                  placeholder="Enter yield percentage"
                  type="number"
                  min={0}
                  max={100}
                  value={yieldValue}
                  onChange={handleYieldChange}
                  disabled={transactionLoading}
                  className="max-w-[200px] bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 focus:ring-primary dark:focus:ring-primary"
                />
                <Button
                  variant="outline"
                  onClick={updateYield}
                  disabled={transactionLoading}
                  className="bg-gradientButton hover:bg-primary/90 text-white dark:bg-primary/80 dark:hover:bg-primary/70 dark:text-black/90 transition-colors duration-200 font-medium shadow-sm hover:shadow-md min-w-[120px]"
                >
                  {transactionLoading ? (
                    <div className="flex items-center gap-2">
                      <CustomLoader
                        size="sm"
                        className="h-4 w-4 animate-spin"
                      />
                      <span>Processing...</span>
                    </div>
                  ) : (
                    "Update Yield"
                  )}
                </Button>
              </div>
            </div>
            {/* todo: dont remove this code its for future use */}
            {/* <div className='bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-6 transition-colors'>
                  <div className='flex justify-between items-center mb-4'>
                    <h4 className='text-sm font-normal text-gray-500 dark:text-gray-400'>
                      Update Asset Price
                    </h4>
                    {selectedAssetDetails && (
                      <div className='flex items-center gap-2'>
                        <span className='text-sm font-medium text-gray-500 dark:text-gray-400'>Current Price:</span>
                        <span className='text-sm font-semibold text-primary bg-secondary px-2 py-1 rounded-md'>
                          {parseFloat(currentPrice).toFixed(2)} USDT
                        </span>
                      </div>
                    )}
                  </div>
                  <div className='flex items-center gap-6 mt-2'>
                    <Input
                      placeholder='Enter new price in USDT'
                      type='number'
                      min={0}
                      value={priceValue}
                      onChange={handlePriceChange}
                      disabled={priceTransactionLoading}
                      className='max-w-[200px] bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 focus:ring-primary dark:focus:ring-primary'
                    />
                    <Button
                      variant='outline'
                      onClick={updatePrice}
                      disabled={priceTransactionLoading}
                      className='bg-gradientButton hover:bg-primary/90 text-white dark:bg-primary/80 dark:hover:bg-primary/70 dark:text-black/90 transition-colors duration-200 font-medium shadow-sm hover:shadow-md min-w-[120px]'
                    >
                      {priceTransactionLoading ? (
                        <div className="flex items-center gap-2">
                          <CustomLoader size="sm" className="h-4 w-4 animate-spin" />
                          <span>Processing...</span>
                        </div>
                      ) : (
                        'Update Price'
                      )}
                    </Button>
                  </div>
                </div> */}

            <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-6 transition-colors">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-normal text-gray-500 dark:text-gray-400 mb-4">
                  Set Yield Reward
                </h4>
                {selectedAssetDetails && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Current Reward Balance:
                      </span>
                      <span className="text-sm font-semibold text-primary bg-secondary px-2 py-1 rounded-md">
                        {parseFloat(tokenBalance).toFixed(0)} USDT
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-6 mt-2">
                <Input
                  placeholder="Enter reward in USDT"
                  type="number"
                  min={0}
                  value={rewardValue}
                  onChange={handleRewardChange}
                  className="max-w-[200px] bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 focus:ring-primary dark:focus:ring-primary"
                />
                <Button
                  variant="outline"
                  onClick={addReward}
                  disabled={rewardTransactionLoading}
                  className="bg-gradientButton hover:bg-primary/90 text-white dark:bg-primary/80 dark:hover:bg-primary/70 dark:text-black/90 transition-colors duration-200 font-medium shadow-sm hover:shadow-md"
                >
                  {rewardTransactionLoading ? (
                    <div className="flex items-center gap-2">
                      <CustomLoader
                        size="sm"
                        className="h-4 w-4 animate-spin"
                      />
                      <span>Processing...</span>
                    </div>
                  ) : (
                    "Add Reward"
                  )}
                </Button>
              </div>
            </div>
            <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-6 transition-colors">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-normal text-gray-500 dark:text-gray-400 mb-4">
                  Update Asset Status
                </h4>
                {selectedAssetDetails && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Current Status:
                    </span>
                    <span className="text-sm font-semibold text-primary bg-primary/10 px-2 py-1 rounded-md">
                      {currentStatus !== null
                        ? ASSET_STATUS_LIST[currentStatus]
                        : "N/A"}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-6 mt-2">
                <Select value={assetStatus} onValueChange={setAssetStatus}>
                  <SelectTrigger className="max-w-[200px]">
                    <SelectValue placeholder="Select new status" />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSET_STATUS_LIST.map((label, idx) => (
                      <SelectItem key={idx} value={String(idx)}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="default"
                  onClick={updateAssetStatus}
                  disabled={statusTransactionLoading}
                  className="bg-gradientButton hover:bg-primary/90 text-white dark:bg-primary/80 dark:hover:bg-primary/70 dark:text-black/90 transition-colors duration-200 font-medium shadow-sm hover:shadow-md"
                >
                  {statusTransactionLoading ? (
                    <div className="flex items-center gap-2">
                      <CustomLoader
                        size="sm"
                        className="h-4 w-4 animate-spin"
                      />
                      <span>Processing...</span>
                    </div>
                  ) : (
                    "Update Status"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateYields;
